// utils
import stringUppercaseFirst from "@cloudspire/shared/utils/string/uppercaseFirst";

// helpers
import bookingEstimateGetDiscountServiceFeeAmount from "@cloudspire/legacy-resources/src/helpers/booking/estimate/getDiscountServiceFeeAmount";
import bookingEstimateHasList from "@cloudspire/legacy-resources/src/helpers/booking/estimate/hasList";
import hotelServiceGetIntlDisplayName from "@cloudspire/shared/helpers/hotel/service/getIntlDisplayName";

/**
 * Récupère les informations à afficher dans la liste des tarifs liés à l'estimation.
 */
export default function bookingEstimateGetList({
  isBookable,
  bookingEstimate,
  intl,
}) {
  if (!bookingEstimateHasList({ bookingEstimate })) {
    return null;
  }

  const list = [];

  // Montant des nuits sans promotion.
  list.push({
    label: stringUppercaseFirst(
      intl.formatMessage(
        {
          defaultMessage: "{nights, plural, one {une nuit} other {# nuits}}",
        },
        { nights: bookingEstimate.duration }
      )
    ),
    value: intl.formatNumber(
      bookingEstimate.customerPeriodsAmountWithoutDiscounts,
      {
        style: "currency",
        currency: "EUR",
      }
    ),
  });

  // Montant de la promotion sur les nuits.
  if (bookingEstimate.metas.customerDiscountsAmount > 0) {
    list.push({
      label: intl.formatMessage({
        defaultMessage: "Promotion",
      }),
      value: intl.formatNumber(-bookingEstimate.metas.customerDiscountsAmount, {
        style: "currency",
        currency: "EUR",
      }),
    });
  }

  // Montant des remises sur les nuits.
  if (bookingEstimate.metas.discount > 0) {
    list.push({
      label: intl.formatMessage({
        defaultMessage: "Remise",
      }),
      value: intl.formatNumber(-bookingEstimate.metas.discount, {
        style: "currency",
        currency: "EUR",
      }),
    });
  }

  // Liste des options.
  const { optionList } = bookingEstimate;

  if (optionList.length > 0) {
    optionList.forEach((option, optionIndex) => {
      const quantity = option.booking_quantity;
      const name = stringUppercaseFirst(
        hotelServiceGetIntlDisplayName({
          intl,
          hotelService: option,
          fallback: intl.formatMessage(
            {
              defaultMessage: "Option {optionIndex}",
            },
            {
              optionIndex: optionIndex + 1,
            }
          ),
        })
      );
      const label =
        quantity > 1 ? `${quantity}\u00a0\u00d7\u00a0${name}` : name;

      list.push({
        label: (
          <div>
            {label}
            {0 < (option?.periods ?? []).length && (
              <div>
                {option.periods.map((period, index) => (
                  <div style={{ fontSize: "0.75rem" }} key={index}>
                    {intl.formatMessage(
                      {
                        defaultMessage:
                          "du {startDay} {startMonth, select, 1 {janvier} 2 {février} 3 {mars} 4 {avril} 5 {mai} 6 {juin} 7 {juillet} 8 {août} 9 {septembre} 10 {octobre} 11 {novembre} 12 {décembre} other {{startMonth}}} au {endDay} {endMonth, select, 1 {janvier} 2 {février} 3 {mars} 4 {avril} 5 {mai} 6 {juin} 7 {juillet} 8 {août} 9 {septembre} 10 {octobre} 11 {novembre} 12 {décembre} other {{endMonth}}}",
                      },
                      {
                        startDay: period.start_day,
                        startMonth: period.start_month,
                        endDay: period.end_day,
                        endMonth: period.end_month,
                      }
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ),
        value: intl.formatNumber(option.booking_amount, {
          style: "currency",
          currency: "EUR",
        }),
      });
    });
  }

  // Assurance
  if (bookingEstimate.insuranceAmount > 0) {
    list.push({
      label: intl.formatMessage({
        defaultMessage: "Assurance annulation",
      }),
      value: intl.formatNumber(bookingEstimate.insuranceAmount, {
        style: "currency",
        currency: "EUR",
      }),
    });
  }

  if (isBookable) {
    // Frais de réservation.
    if (bookingEstimate.baseServiceFeeAmount !== -1) {
      list.push({
        label: intl.formatMessage({
          defaultMessage: "Frais de réservation",
        }),
        value:
          bookingEstimate.baseServiceFeeAmount === 0
            ? intl.formatMessage({
                defaultMessage: "offert",
              })
            : intl.formatNumber(bookingEstimate.baseServiceFeeAmount, {
                style: "currency",
                currency: "EUR",
              }),
      });
    }

    const discountServiceFeeAmount = bookingEstimateGetDiscountServiceFeeAmount(
      {
        serviceFeeAmount: bookingEstimate.serviceFeeAmount,
        baseServiceFeeAmount: bookingEstimate.baseServiceFeeAmount,
      }
    );

    // Promotion frais de réservation.
    if (discountServiceFeeAmount < 0) {
      list.push({
        label: intl.formatMessage({
          defaultMessage: "Promotion frais de réservation",
        }),
        value: intl.formatNumber(discountServiceFeeAmount, {
          style: "currency",
          currency: "EUR",
        }),
      });
    }
  }

  // Taxe de séjour.
  if (bookingEstimate.touristTaxAmount > 0) {
    list.push({
      label: intl.formatMessage({
        defaultMessage: "Taxe de séjour",
      }),
      value: intl.formatNumber(bookingEstimate.touristTaxAmount, {
        style: "currency",
        currency: "EUR",
      }),
    });
  }

  return list;
}
